import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Mockup } from '../interfaces/mockup.interface';
import { environment } from "../../../environments/environment";

export interface KPISlice {
  label: string;
  count: number;
  color: string;
}

export interface KPIData {
  totalProcesses: number;
  filesWithErrors: number;
  meanErrorsByFile: number;
  byDescription: KPISlice[];
  errorDistribution: KPISlice[];
  dailyData: { date: string; count: number; cumulative: number }[];
}

const EXT_COLORS = [
  '#0ab2d7', '#274693', '#7b4fd5', '#ff6b6b',
  '#e8b4b8', '#6dbf4d', '#f0a500', '#ff9f43',
  '#54a0ff', '#5f27cd',
];
const ERR_COLORS = ['#0ab2d7', '#6dbf4d', '#7b4fd5', '#ff6b6b', '#f0a500'];

@Injectable({ providedIn: 'root' })
export class SimulationService {
  private supabaseService = inject(SupabaseService);

  async saveProcess(
    type: string | null,
    mockup: Mockup,
    profile: string | null = null,
    errors: string[],
  ): Promise<void> {
    if (!type) return;
    try {
      const fileType = type.startsWith('video/') ? 'video' : type.startsWith('image/') ? 'image' : 'pdf';
      const extension = type.split('/')[1] || null;
      await this.supabaseService
        .supabase
        .from('processes')
        .insert({
          type: fileType,
          extension,
          mockup,
          profile,
          production: environment.production,
          CALERR_00001: errors.includes('CALERR_00001'),
          CALERR_00002: errors.includes('CALERR_00002'),
          CALERR_00003: errors.includes('CALERR_00003'),
          CALERR_00004: errors.includes('CALERR_00004'),
          CALERR_00005: errors.includes('CALERR_00005'),
          CALERR_00006: errors.includes('CALERR_00006'),
          CALERR_00007: errors.includes('CALERR_00007'),
          CALERR_00008: errors.includes('CALERR_00008'),
          CALERR_00010: errors.includes('CALERR_00010'),
          CALERR_00011: errors.includes('CALERR_00011'),
          CALERR_00012: errors.includes('CALERR_00012'),
          CALERR_00013: errors.includes('CALERR_00013'),
          CALERR_00014: errors.includes('CALERR_00014'),
        })
        .throwOnError();
    } catch (e) {
      console.error('Error saving process:', e);
      throw e;
    }
  }

  async getKPIData(): Promise<KPIData> {
    const { data, error } = await this.supabaseService.supabase.functions.invoke(
      'get_analytics',
      { body: environment.production ? {} : { production: false } });
    if (error) throw error;

    const byDescription: KPISlice[] = (data.byDescription ?? []).map(
      (s: { label: string; count: number }, i: number) => ({
        ...s,
        color: EXT_COLORS[i % EXT_COLORS.length],
      }),
    );

    const errorDistribution: KPISlice[] = (data.errorDistribution ?? []).map(
      (s: { value: number; count: number }, i: number) => ({
        label: s.value,
        count: s.count,
        color: ERR_COLORS[i % ERR_COLORS.length],
      }),
    );

    return {
      totalProcesses: data.totalProcesses ?? 0,
      filesWithErrors: data.filesWithErrors ?? 0,
      meanErrorsByFile: data.meanErrorsByFile ?? 0,
      byDescription,
      errorDistribution,
      dailyData: data.dailyData ?? [],
    };
  }
}
